import React, { useState, useEffect, useRef } from 'react';
import { Select, Spin } from 'antd';
import { SearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useProductOpenApiClient from '../../../apiClients/useProductOpenApiClient';
import { components } from '../../../apiClients/base/schema';

const { Option } = Select;

type ProductResponse = components["schemas"]["ProductResponse"];

const SearchDropdown: React.FC = () => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [results, setResults] = useState<ProductResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const { searchProduct } = useProductOpenApiClient();
  const selectRef = useRef<any>(null);

  // Keyboard shortcut to focus search (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        if (selectRef.current) {
          selectRef.current.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Search products with debounce
  useEffect(() => {
    if (searchValue && searchValue.length >= 2) {
      setLoading(true);
      
      // Debounce search - reduced to 300ms
      const timer = setTimeout(async () => {
        try {
          const searchResults = await searchProduct(searchValue);
          setResults(searchResults || []);
        } catch (error) {
          console.error('Search error:', error);
          setResults([]);
        } finally {
          setLoading(false);
        }
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setResults([]);
      setLoading(false);
    }
  }, [searchValue]);

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleSelect = (value: string) => {
    const selected = results.find(item => item.id?.toString() === value);
    if (selected && selected.id) {
      // Navigate to product edit page
      navigate(`/products/edit/${selected.id}`);

      setSearchValue('');
      setResults([]);
    }
  };

  const handleClear = () => {
    setSelectedValue(undefined);
    setSearchValue('');
    setResults([]);
    setLoading(false);
  };

  const getImageUrl = (product: ProductResponse): string => {
    return product.images?.[0]?.previewUrl || '';
  };

  return (
    <div style={{width: '300px', margin: '0 auto' }}>
      <style>{`
        .vb-search-dropdown {
          .ant-select-selector {
            margin-top: 10px !important;
          }
          .ant-select-clear {
            margin-top: 3px !important;
            scale: 1.5;
          }
          .ant-select-arrow {
            margin-top: 3px !important;
            scale: 1.5;
          }
        }
      `}</style>
      <Select
        className="vb-search-dropdown"
        key={selectedValue || 'empty'} // Force re-render when selectedValue changes
        showSearch
        value={selectedValue}
        placeholder="Sök efter produkt..."
        style={{ width: '100%' }}
        size="large"
        suffixIcon={loading ? <LoadingOutlined spin /> : <SearchOutlined />}
        filterOption={false}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onClear={handleClear}
        allowClear
        loading={loading}
        notFoundContent={
          loading ? (
            <div style={{ textAlign: 'center', padding: '12px' }}>
              <Spin size="small" />
              <span style={{ marginLeft: '8px' }}>Söker produkter...</span>
            </div>
          ) : searchValue && searchValue.length >= 2 && results.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '12px', color: '#999' }}>
              Inga produkter hittades
            </div>
          ) : searchValue && searchValue.length < 2 ? (
            <div style={{ textAlign: 'center', padding: '12px', color: '#999' }}>
              Skriv minst 2 tecken för att söka
            </div>
          ) : null
        }
        dropdownStyle={{
          maxHeight: '300px',
          overflow: 'auto',
        }}
        getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
        ref={selectRef}
      >
        {results.map((item) => (
          <Option key={item.id} value={item.id?.toString()}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '2px 0' }}>
              {getImageUrl(item) && (
                <img 
                  src={getImageUrl(item)} 
                  alt={item.name || ''} 
                  style={{ 
                    width: '30px', 
                    height: '30px', 
                    objectFit: 'cover', 
                    borderRadius: '3px', 
                    marginRight: '8px',
                    flexShrink: 0
                  }} 
                />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <div style={{ fontWeight: 'bold', fontSize: '13px', lineHeight: '1.2' }}>
                  {item.name || 'Namn saknas'}
                </div>
                <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.1' }}>
                  {item.mainItemNo && `SKU: ${item.mainItemNo}`}
                  {item.brandName && ` • ${item.brandName}`}
                </div>
              </div>
            </div>
          </Option>
        ))}
      </Select>
    </div>
  );
};

export default SearchDropdown;
